import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { billingService } from '../services/billingService'
import type { Patient } from '../types/patient'
import type { BillingAccount, BillableItem, BillingPayment, BillingStatement } from '../types/billing'
import './PatientBillingStatementPrint.css'

export function PatientBillingStatementPrint() {
  const { id } = useParams<{ id: string }>()

  const { data: patient, isLoading: loadingPatient, error: patientError } = useQuery<Patient>({
    queryKey: ['patient', id],
    queryFn: () => patientService.getById(id!),
    enabled: !!id,
  })

  const { data: accounts = [], isLoading: loadingAccounts } = useQuery<BillingAccount[]>({
    queryKey: ['billing-accounts', id, 'print'],
    queryFn: () => billingService.getAccounts({ patientId: id! }),
    enabled: !!id,
  })

  const { data: items = [], isLoading: loadingItems } = useQuery<BillableItem[]>({
    queryKey: ['billing-items', id, 'print'],
    queryFn: () => billingService.getItems({ patientId: id! }),
    enabled: !!id,
  })

  const { data: payments = [], isLoading: loadingPayments } = useQuery<BillingPayment[]>({
    queryKey: ['billing-payments', id, 'print'],
    queryFn: () => billingService.getPayments({ patientId: id! }),
    enabled: !!id,
  })

  const { data: statements = [], isLoading: loadingStatements } = useQuery<BillingStatement[]>({
    queryKey: ['billing-statements', id, 'print'],
    queryFn: () => billingService.getStatements({ patientId: id! }),
    enabled: !!id,
  })

  const isLoading = loadingPatient || loadingAccounts || loadingItems || loadingPayments || loadingStatements
  const error = patientError

  const primaryAccount = accounts[0]
  const latestStatement = useMemo(() => {
    if (!statements.length) return null
    return statements.slice().sort((a, b) => (a.statementDate > b.statementDate ? -1 : 1))[0]
  }, [statements])

  const totals = useMemo(() => {
    const charges = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
    const paid = payments.reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0)
    return { charges, paid, balance: charges - paid }
  }, [items, payments])

  useEffect(() => {
    if (!isLoading && !error) {
      const t = setTimeout(() => {
        window.print()
      }, 300)
      return () => clearTimeout(t)
    }
  }, [isLoading, error])

  if (!id) {
    return <div className="print-wrapper">Invalid patient.</div>
  }

  if (isLoading) {
    return <div className="print-wrapper">Preparing statement...</div>
  }

  if (error || !patient) {
    return <div className="print-wrapper">Unable to load billing statement.</div>
  }

  const currency = primaryAccount?.currency || 'EUR'

  return (
    <div className="print-wrapper">
      <div className="statement-page">
        <header className="statement-header">
          <div>
            <h1>Statement of Account</h1>
            <p>Patient: {patient.firstName} {patient.lastName}</p>
            <p>Patient ID: {patient.id}</p>
          </div>
          <div className="statement-meta">
            <div><strong>Statement Date:</strong> {latestStatement?.statementDate || new Date().toISOString().split('T')[0]}</div>
            <div><strong>Account:</strong> {primaryAccount?.id || 'Not created'}</div>
            <div><strong>Status:</strong> {latestStatement?.status || 'draft'}</div>
          </div>
        </header>

        <section className="statement-summary">
          <div>
            <label>Total Charges</label>
            <span>{new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(totals.charges)}</span>
          </div>
          <div>
            <label>Total Payments</label>
            <span>{new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(totals.paid)}</span>
          </div>
          <div>
            <label>Balance</label>
            <span>{new Intl.NumberFormat('en-IE', { style: 'currency', currency }).format(primaryAccount?.balance ?? totals.balance)}</span>
          </div>
        </section>

        <section className="statement-section">
          <h2>Billable Items</h2>
          {items.length === 0 ? (
            <p>No billable items recorded.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Consultation</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.description}</td>
                    <td>{item.consultationId || '-'}</td>
                    <td>{item.status}</td>
                    <td className="amount">
                      {new Intl.NumberFormat('en-IE', { style: 'currency', currency: item.currency }).format(item.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="statement-section">
          <h2>Payments</h2>
          {payments.length === 0 ? (
            <p>No payments recorded.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Provider</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{payment.paymentDate.split('T')[0]}</td>
                    <td>{payment.provider}</td>
                    <td>{payment.status}</td>
                    <td className="amount">
                      {new Intl.NumberFormat('en-IE', { style: 'currency', currency: payment.currency }).format(payment.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  )
}
